import React, { useEffect, useState } from "react";
import { Auth, API } from "aws-amplify";
import styled, { keyframes } from "styled-components";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import DefaultLayout from "../layouts/DefaultLayout";
import { useTranslation } from "react-i18next";
import { emailIsValid, fetchQuery } from "../functions/misc";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Message from "../components/Message";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Spinner } from "react-bootstrap";
import { AnimatePresence, motion } from 'framer-motion';
import Loading from "../Common/Loading";

interface FormError {
  email: string;
  passcode: string;
  newPasscode: string;
  password: string;
  confirmPassword: string;
}
interface AnimatedDotProps {
  delay: string;
}

type ResponseData = {
  token: string;
  passcode: string;
  triggerSource: string;
  email: string;
  userId: string;
  valid: boolean;
};

enum TriggerSource {
  CustomEmailSender_SignUp = "CustomEmailSender_SignUp",
  CustomEmailSender_ForgotPassword = "CustomEmailSender_ForgotPassword",
  CustomEmailSender_ResendCode = "CustomEmailSender_ResendCode"
}

const VerifyAccount = () => {
  const STEP = {
    init: "init",
    sentCode: "sent_code",
    updatePassword: "updatePassword",
    done: "done"
  };
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");

  const [error, setError] = useState<FormError>({
    email: "",
    passcode: "",
    newPasscode: "",
    password: "",
    confirmPassword: "",
  });
  const [submited, setSubmited] = useState(false);
  const [step, setStep] = useState(STEP.sentCode);
  let location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [submitted2, setSubmitted2] = useState(false);
  const [isResent, setIsResent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [pwdIcon, setPwdIcon] = useState(faEyeSlash);
  const [pwdType, setPwdType] = useState("password");
  const [confirmPwdIcon, setConfirmPwdIcon] = useState(faEyeSlash);
  const [confirmPwdType, setConfirmPwdType] = useState("password");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [ShowSpinner, setShowSpinner] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [limitExceed, setLimitExceeded] = useState(false);
  const [passcodeInvalid, setPasscodeInvalid] = useState(false);
  const [showSignUpMessage, setShowSignUpMessage] = useState(true);
  const [responseData, setResponseData] = useState<ResponseData | null>(null);
  const passcodeParam = searchParams.get("Token");

  useEffect(() => {
    validateForm(email, verifyCode, password, confirmPassword);
  }, [email, verifyCode, password, confirmPassword]);

  useEffect(() => {
    if (passcodeParam) {
      handlePasscodeVerification(passcodeParam);
      setStep(STEP.updatePassword);
    } else {
      setIsLoading(false);
    }
  }, [location]);

  const handlePasscodeVerification = async (token: string) => {
    const endpoint = `${process.env.REACT_APP_VALIDATE_TOKEN_ENDPOINT}/validate-reset-password-token`;
    if (!endpoint) {
      console.log("endpoint is not defined");
      return;
    }

    try {
      const response = await fetch(endpoint,
        {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Token: token,
        })
      });

      if (!response.ok) {
        console.log(`Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (data) {
        if (data.valid) {
          setResponseData({
            token: data.token,
            passcode: data.passcode,
            triggerSource: data.triggerSource,
            email: data.email,
            userId: data.userId,
            valid: data.valid,
          });
          setEmail(data.userId);
          setVerifyCode(data.passcode);
          if (data.triggerSource === TriggerSource.CustomEmailSender_ForgotPassword) {
            setShowSignUpMessage(false);
            setStep(STEP.updatePassword);
            setIsLoading(false);
          } else if (data.triggerSource === TriggerSource.CustomEmailSender_SignUp || data.triggerSource === TriggerSource.CustomEmailSender_ResendCode) {
            await handleUserSignUp(data.userId, data.passcode);
          }
        } else {
          setResponseData({
            token: data.token,
            passcode: data.passcode,
            triggerSource: data.triggerSource,
            email: data.email,
            userId: data.userId,
            valid: data.valid,
          });
          setEmail(data.userId);
          setStep(STEP.sentCode);
          setPasscodeInvalid(true);
          setIsLoading(false);
        }
      }
    } catch (error) {
      setStep(STEP.sentCode);
      setPasscodeInvalid(true);
      setIsLoading(false);
      console.error('Error:', error);
    }
  };


  const validateForm = (
    _email: string,
    _passcode: string,
    _password: string,
    _confirmpassword: string
  ) => {
    let _error = error;
    if (!_email) {
      _error["email"] = t("forgetPage.error.email.empty");
    } else if (!emailIsValid(_email)) {
      _error["email"] = t("forgetPage.error.email.invalid");
    } else {
      _error["email"] = "";
    }

    if (!_passcode) {
      _error["passcode"] = t("forgetPage.error.passcode.empty");
    } else {
      _error["passcode"] = "";
    }

    if (!_password) {
      _error["password"] = t("forgetPage.error.password.empty");
    } else {
      _error["password"] = "";
    }

    if (!_confirmpassword) {
      _error["confirmPassword"] = t("forgetPage.error.confirmPassword.empty");
    } else if (_password !== _confirmpassword) {
      _error["confirmPassword"] = t(
        "forgetPage.error.confirmPassword.mismatch"
      );
    } else {
      _error["confirmPassword"] = "";
    }

    setError(_error);
    setError({ ...error });
  };

  const handleInputPasscode = (e: any) => {
    setVerifyCode(e.target.value);
  };


  const userNotFoundException = () => {
    const errorCopy = { ...error };
    errorCopy.email = "Email doesn't exist";
    setError(errorCopy);
  };


  const handleInputPassword = (e: any) => {
    setPassword(e.target.value);
  };

  const handleInputConfirmPassword = (e: any) => {
    setConfirmPassword(e.target.value);
  };

  const togglePassword = () => {
    if (pwdType === "password") {
      setPwdType("text");
      setPwdIcon(faEye);
      return;
    }
    setPwdType("password");
    setPwdIcon(faEyeSlash);
  };

  const toggleConfirmPassword = () => {
    if (confirmPwdType === "password") {
      setConfirmPwdType("text");
      setConfirmPwdIcon(faEye);
      return;
    }
    setConfirmPwdType("password");
    setConfirmPwdIcon(faEyeSlash);
  };

  const resetPassword = async () => {
    setShowSpinner(true);
    setSubmitted2(true);
    setDisableSubmit(true);
    if (error.password || error.confirmPassword) {
      setShowSpinner(false);
      setDisableSubmit(false);
      const errorCopy = { ...error };
      return setError(errorCopy);
    }

      await handleUserResetPassword();
  };

  const handleUserSignUp = async (signUpEmail: string, sigUpCode: string) => {
    try {
      await Auth.confirmSignUp(signUpEmail, sigUpCode);
      setTimeout(() => {
        navigate('/login', { state: { accountCreated: true } });
        setStep(STEP.done)
      }, 2000);
    } catch (error: any) {
      console.log("error", error);
    }
  };



  const handleUserResetPassword = async () => {
    Auth.forgotPasswordSubmit(email, verifyCode, password)
      .then((res) => {
        setTimeout(() => {
          navigate('/login', { state: { passwordReset: true } });
          setStep(STEP.done)
        }, 2000);
      })
      .catch((error) => {
        setShowSpinner(false);
        setDisableSubmit(false);
        const code = error.code;
        if (code === "CodeMismatchException") {
          setStep(STEP.sentCode);
          const errorCopy = { ...error };
          errorCopy.passcode =
            "The code entered is invalid. Please verify the code or request a new passcode.";
          return setPasscodeInvalid(true);
        }

        if (code === "InvalidPasswordException") {
          const errorCopy = { ...error };
          errorCopy.password = error.message;
          const policyPrefix = "Password does not conform to policy:";
          if (error.message.includes(policyPrefix)) {
            errorCopy.password = error.message.replace(policyPrefix, "").trim();
          }

          return setError(errorCopy);
        }

        if(code === "LimitExceededException") {
          const errorCopy = { ...error };
          setDisableSubmit(true);
          setLimitExceeded(true);
          return setError(errorCopy);
        }
      });
  }

  const motionContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.1, ease: "easeInOut" } },
    exit: { opacity: 0, transition: { duration: 0.1, ease: "easeInOut" } }
  };

  const motionItem = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const resendPasscode = () => {
    setIsResent(false);
    Auth.forgotPassword(email?.toLocaleLowerCase() || "")
      .then((res) => {
        setIsResent(true);
      })
      .catch((err) => {
        const code = err.code;
        setDisableSubmit(false);
        switch (code) {
          case "LimitExceededException":
            setDisableSubmit(true);
            setIsResent(false);
            setLimitExceeded(true);
            const errorCopy = { ...error };
            return setError(errorCopy);
          default:
            setIsResent(false);
            return false;
        }
      });
  };

  return (
    <DefaultLayout>
      <AnimatePresence mode="wait">
        {isLoading ?
          <PageBody>
            <Loading />
            {showSignUpMessage &&
              <AnimatePresence>
                <motion.div className='mt-4' variants={motionItem}>
                  <motion.p
                    key={"feedbackPrompt"}
                    className="content-text mt-2 mb-4 h6 text-center"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ ease: "easeInOut", duration: 0.1 }}
                  >
                    Verifying your email—please
                    < br/>
                    wait to proceed to the next step&nbsp;
                    <span style={{ whiteSpace: 'nowrap' }}>
                    <AnimatedDot delay="0s">.</AnimatedDot>
                    <AnimatedDot delay="0.5s">.</AnimatedDot>
                    <AnimatedDot delay="1s">.</AnimatedDot>
                  </span>
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            }
          </PageBody>
          :
          <>
            {step === STEP.done && (
              <motion.div key={STEP.done} variants={motionContainer} initial="hidden" animate="visible" exit="exit">
                <PageBody>
                  <form onSubmit={(e) => e.preventDefault()}>
                    <FormTitle>{t("forgetPage.verifySuccess")}</FormTitle>
                    <Button
                  className="w-100 mt-4 mt-sm-4"
                  variant="primary"
                  onClick={(e) => {
                    navigate("/login");
                  }}
                >
                  Continue
                </Button>
              </form>
            </PageBody>
          </motion.div>)}
            {step === STEP.sentCode && (
              <motion.div key={STEP.sentCode} variants={motionContainer} initial="hidden" animate="visible" exit="exit">
                <PageBodyResent>
                  <FormTitle className='lh-base'>Oops! It looks like there's a problem with your reset link.</FormTitle>
                  <NotAccount>
                    <TextButton onClick={resendPasscode}>
                      No worries—just click here, and we'll send you a new one.
                    </TextButton>
                  </NotAccount>
                  {isResent && (
                    <Message
                      message={t("verifyCodePage.resentMessage")}
                      type="success"
                    ></Message>
                  )}
                </PageBodyResent>
              </motion.div>
            )}
            {step === STEP.updatePassword && (
              <PageBody>
                <motion.div key={STEP.updatePassword} variants={motionContainer} initial="hidden" animate="visible"
                            exit="exit">
                  <FormTitle>Set a New Password</FormTitle>
                  <p className="content-text">You're just one step away from securing your account. Please create a new
                    password below.</p>
                  <p className='content-text fw-bold'>Password Requirements:</p>
                  <ul>
                    <li className='content-text'>Must be at least 8 characters long.</li>
                    <li className='content-text'>Include at least one uppercase letter, one number, and one special character.</li>
                  </ul>
                  <FormGroup className="" id="password">
                    <FormLabel>Password</FormLabel>
                    <div className="position-relative mb-3">
                      <Form.Control
                        value={password}
                        type={pwdType}
                        placeholder={t("forgetPage.password")}
                        onChange={handleInputPassword}
                        className={
                          "input-no-bg " +
                          (submitted2 && error.password ? "is-invalid" : "")
                        }
                      />
                      <FontAwesomeIcon
                        onClick={togglePassword}
                        className="addOn"
                        icon={pwdIcon}
                      />
                    </div>
                    {submitted2 && error.password && (
                      <motion.div key="errorPassword" variants={motionContainer} initial="hidden" animate="visible"
                                  exit="exit">
                        <Message message={error.password} type="error"></Message>
                      </motion.div>
                    )}

                    {submited && error.passcode && (
                      <motion.div key="errorSubmittedPassword" variants={motionContainer} initial="hidden"
                                  animate="visible"
                                  exit="exit">
                        <Message message={error.passcode} type="error"></Message>
                      </motion.div>
                    )}
                  </FormGroup>
                  <FormGroup className="mb-3 mt-2" id="confirmpassword">
                    <FormLabel>{t("forgetPage.confirmpassword")}</FormLabel>
                    <div className="position-relative">
                      <Form.Control
                        value={confirmPassword}
                        type={confirmPwdType}
                        placeholder={t("forgetPage.confirmpassword")}
                        onChange={handleInputConfirmPassword}
                        className={
                          "input-no-bg " +
                          (submitted2 && error.confirmPassword ? "is-invalid" : "")
                        }
                      />
                      <FontAwesomeIcon
                        onClick={toggleConfirmPassword}
                        className="addOn"
                        icon={confirmPwdIcon}
                      />
                    </div>
                    {submitted2 && error.confirmPassword && (
                      <motion.div key="errorConfirmPassword" variants={motionContainer} initial="hidden"
                                  animate="visible"
                                  exit="exit">
                        <Message message={error.confirmPassword} type="error"></Message>
                      </motion.div>
                    )}
                    {submitted2 && limitExceed && (
                      <motion.div key="limitExceed" variants={motionContainer} initial="hidden" animate="visible"
                                  exit="exit">
                        <Message message={"Attempt limit exceeded, please try again after 15 minutes."}
                                 type="error"></Message>
                      </motion.div>
                    )}
                  </FormGroup>
                  <p className='content-text mt-4'>
                    <strong>Security Reminder: </strong>
                    For your security, please choose a password that you haven’t used before.</p>
                  <Button className="w-100 mt-3 mt-sm-4" disabled={disableSubmit} onClick={resetPassword}>
                    {ShowSpinner ?
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-4"
                        />
                        {'  '}
                      </>
                      :
                      <>
                        Set Password
                      </>
                    }
                  </Button>
                </motion.div>
              </PageBody>
            )}
          </>
        }
      </AnimatePresence>
    </DefaultLayout>
  );
};

export default VerifyAccount;

const PageBody = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 5rem 2rem;
  min-height: 500px;
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  @media (max-width: 800px) {
    padding: 2rem 2rem !important;
    min-height: 0 !important;
  }
  @media (max-width: 600px) {
    padding: 1rem 2rem !important;
    min-height: 0 !important;
  }
`;

const PageBodyResent = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 5rem 2rem;
  min-height: 500px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  @media (max-width: 800px) {
    padding: 2rem 2rem !important;
    min-height: 0 !important;
  }
  @media (max-width: 600px) {
    padding: 1rem 2rem !important;
    min-height: 0 !important;
  }
`;

const FormTitle = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  color: #0d4d90;
  font-family: "Montserrat", sans-serif;
  @media (max-width: 425px) {
   margin-top: 1rem;
  }
`;

const FormGroup = styled.div``;
const FormLabel = styled.label`
  font-size: 0.8rem;
  margin-bottom: 0.4rem;
  @media (max-width: 425px) {
    margin-top: 0.5rem;
  }
`;

const dotAnimation = keyframes`
  0% {
    transform: translateY(0);
  }
  33% {
    transform: translateY(-5px);
  }
  66% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(0);
  }
`;

const AnimatedDot = styled.span<AnimatedDotProps>`
  display: inline-block;
  margin-right: 2px;
  font-weight: bold;
  font-size: 14px;
  animation: ${dotAnimation} 1.5s infinite ease-in-out;
  animation-delay: ${(props) => props.delay || '0s'};
`;

const TextButton = styled.span`
  text-align: center;
  cursor: pointer;
`;

const NotAccount = styled.div`
  margin-top: 1rem;
  font-size: 1rem;
  text-align: start;
  text-decoration: underline;
  color: #1589D2;
`;

const Email = styled.label`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 1rem;
  @media (max-width: 600px) {
    font-size: 1rem !important;
  }
  @media (max-width: 320px) {
    font-size: 0.8rem !important;
  }
`;

const TextButtonRequest = styled.span`
  display: inline;
  padding: 4px 18px;
  border: 1px solid #cfd8dc;
  border-radius: 16px;
  background: #cfd8dc;
  letter-spacing: -0.12px;
  color: #4c5360;
  opacity: 1;
  cursor: pointer;
`;